/* eslint-disable @typescript-eslint/no-explicit-any */
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import {
  type DefaultError,
  type QueryKey,
  type UseQueryOptions,
  useQuery,
} from '@tanstack/react-query';

import { type Client, clients } from '../utils/graphql';

type BatchQueryItem<TQuery extends TypedDocumentNode<any, any>> =
  TQuery extends TypedDocumentNode<any, infer TVariables>
    ? TVariables extends Record<string, never>
      ? { document: TQuery; variables?: never }
      : { document: TQuery; variables: TVariables }
    : never;

type BatchQueryInput = BatchQueryItem<TypedDocumentNode<any, any>> | false | null | undefined;

// Helper type to extract data type from a query item
type ExtractDataFromBatchItem<T> =
  T extends BatchQueryItem<TypedDocumentNode<infer TData, any>> ? TData : undefined;

// Result type that maps each input to its corresponding data type
type BatchQueryResult<TQueries extends readonly BatchQueryInput[]> = {
  [K in keyof TQueries]: ExtractDataFromBatchItem<TQueries[K]>;
};

function isValidBatchQuery(
  item: BatchQueryInput,
): item is BatchQueryItem<TypedDocumentNode<any, any>> {
  return Boolean(item && typeof item === 'object' && 'document' in item);
}

// Type-safe batch query builder that also transforms to request format
export const prepareBatchQuery = <TData, TVariables extends Record<string, unknown>>(
  query: TypedDocumentNode<TData, TVariables>,
  variables?: TVariables extends Record<string, never> ? undefined : TVariables,
): BatchQueryItem<typeof query> => ({ document: query, variables }) as BatchQueryItem<typeof query>;

/*  Beware when using this hook with a large number of queries and/or with complex queries
    as it may impact performance and lead to increased latency and potentially timeouts. */
export function useGqlQueries<
  TQueries extends readonly BatchQueryInput[],
  TError = DefaultError,
  TData = BatchQueryResult<TQueries>,
  TResult extends BatchQueryResult<TQueries> = BatchQueryResult<TQueries>,
>(
  queries: TQueries,
  options?: Omit<UseQueryOptions<TResult, TError, TData, QueryKey>, 'queryKey' | 'queryFn'> & {
    context?: { clientName: Client };
  },
) {
  const { context, ...rest } = options ?? {};
  const client = clients[context?.clientName ?? 'processor'];

  // Filter out falsy queries
  const validQueries = queries.filter(isValidBatchQuery);
  const queryKey: QueryKey = [validQueries.map(({ document, variables }) => [document, variables])];

  const queryFn = async (): Promise<TResult> => {
    if (validQueries.length === 0)
      // Return array of undefined matching the input structure
      return Array.from({ length: queries.length }) as unknown as TResult;

    const results = await client.batchRequests(validQueries);

    // Map results back to original array structure, filling in undefined for skipped queries
    let i = 0;
    return queries.map((query) =>
      // eslint-disable-next-line no-plusplus
      isValidBatchQuery(query) ? results[i++].data : undefined,
    ) as unknown as TResult;
  };

  return useQuery({
    ...rest,
    queryKey,
    queryFn,
  });
}
